/* eslint-disable no-unused-vars */
import React, {useCallback, useContext, useEffect, useState} from 'react';
import ReactDOMServer from 'react-dom/server';
import {isMobile} from 'react-device-detect';
import {useTranslation} from 'react-i18next';
import {useHistory} from 'react-router-dom';
import {Subject} from 'rxjs';
import {StopModel} from '../../core/models/stop.model';
import {debounceTime, skip, takeUntil} from 'rxjs/operators';
import {
    INIT_COORDS,
    MAP_VIEW,
    MARKER_HEIGHT,
    MARKER_SELECTED_HEIGHT,
    MARKER_SELECTED_WIDTH,
    MARKER_WIDTH,
} from '../../core/constants/map.constants';
import {MessageKeys} from '../../core/constants/map.enums';
import {useQuery} from '../../core/hooks/query.hook';
import {GoogleApiLoader} from '../../core/map/google-maps-api.loader';
import {Map} from '../../core/map/map';
import {DataContext} from '../data-v2/data.context';
import {ThemeContext} from '../theme/themeContext';
import './clusters.scss';
import {MapProvider} from './map.context';
import InfoWindow from './infoWindow/infowindow';
import {
    changeMarkerAccesColor,
    changeMarkerSize,
    createAccesMaker,
    createMaker,
    hideOrShowOnMarkersOnViewport,
} from './map.marker';
import {useLocation} from 'react-router-dom';
import {LayerContext} from '../layer/layer.context';

export const MapService = (props: any) => {

    const {t} = useTranslation();
    const history = useHistory();
    const query = useQuery();
    const location = useLocation();

    const {getTheme} = useContext(ThemeContext);
    const {
        db,
        pinSet,
        currentStop,
        currentStopEntries,
        visibleStops,
        closeStops,
        currentRoute,
        tripCoords,
        currentTrip,
        setCurrentTrip,
        realTimes,
    } = useContext(DataContext);

    const [map, setMap] = useState<any>();
    const [geolocationGranted, setGeolocationGranted] = useState<boolean>(false);
    const [ready, setReady] = useState<boolean>(false);
    const {toggleVisibleBikeLayer} = useContext(LayerContext);
    const [isNoParkingActive, setIsNoParkingActive] = useState<boolean>(false);
    const [noParkingActive, setNoParkingActive] = useState(false);
    const [transportListActivatedOnce, setTransportListActivatedOnce] = useState(false);

    const [currentLocation, setCurrentLocation] = useState<google.maps.LatLngLiteral>(INIT_COORDS);
    const [pointer, setPointer] = useState<google.maps.LatLngLiteral>(INIT_COORDS);

    const [view, setView] = useState<MAP_VIEW>(MAP_VIEW.STOPS);

    const [warningLocationVisibility, setWarningLocationVisibility] = useState<MessageKeys>(MessageKeys.NONE);

    const unsubscribe$: Subject<boolean> = new Subject<boolean>();

    const [markerType, setMarkerType] = useState({});

    const [markerList, setMarkerList] = useState<any>({});

    const [accessList, setAcessList] = useState<any>([]);

    const init = useCallback(async (element: HTMLElement): Promise<Map | undefined> => {

        let coreMap = map;

        const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
        const checkGeolocation = async () => {
            if (navigator.permissions && !isSafari) {
                try {
                    const permission = await navigator.permissions.query({name: 'geolocation'});
                    setGeolocationGranted(permission.state === 'granted');

                    permission.onchange = async () => {
                        const updatedPermission = await navigator.permissions.query({name: 'geolocation'});
                        setGeolocationGranted(updatedPermission.state === 'granted');
                    };
                } catch (err) {
                    console.error('Error al consultar permisos:', err);
                    setGeolocationGranted(false);
                }
            } else {
                navigator.geolocation.getCurrentPosition(
                    () => setGeolocationGranted(true),
                    (err) => {
                        if (err.code === err.PERMISSION_DENIED) {
                            setGeolocationGranted(false);
                        } else {
                            console.error('Error de geolocalización:', err);
                            setGeolocationGranted(false);
                        }
                    },
                );
            }
        };

        await checkGeolocation();

        if (element && !coreMap) {
            await GoogleApiLoader.loadScript();
            coreMap = new Map(element, getTheme());
            setMap(coreMap);
        }

        setReady(true);

        return coreMap;
    }, [map]);

    const goToCurrentLocation = useCallback(async (): Promise<void> => {

        if (navigator.geolocation && !query.lat && !query.lng) {
            try {
                await map?.setCurrentLocationMarker();
                setWarningLocationVisibility(MessageKeys.NONE);
                setCurrentLocation(map?.currentLocation || INIT_COORDS);
                setPointer(map?.currentLocation || INIT_COORDS);
            } catch (e) {
                setWarningLocationVisibility(MessageKeys.ENABLE);
                if (!isMobile) {
                    alert(t('commons_dialog_enable_location_message'));
                }
                throw e;
            }

        } else if (!query.lat && !query.lng) {
            setWarningLocationVisibility(MessageKeys.NO_SUPPORT);
            throw new Error('Geolocation not supported by browser');
        }
    }, [map, query]);

    const boundsHandler = useCallback((bounds: google.maps.LatLngBoundsLiteral) => {
        db?.dataSources?.stops.setBounds(bounds);
    }, [db]);

    const pointerHandler = useCallback((pointer: google.maps.LatLngLiteral | null) => {
        if (pointer) {
            history.push(`/serveis-aprop?type=${query.type || '0'}&lat=${pointer.lat}&lng=${pointer.lng}`);
        }
    }, [query, db]);

    const drawRouteTrips = useCallback(async () => {

        map?.clearTrips();
        map?.clearRealTimeMarkers();


        if (tripCoords.length && currentRoute) {

            tripCoords
                .forEach((trip, index) => {

                    const polyline = new google.maps.Polyline({
                        path: trip.polyline,
                        geodesic: true,
                        strokeColor: trip.index === currentTrip ? currentRoute?.color : '#6d6d6d',
                        strokeOpacity: 1.0,
                        strokeWeight: trip.index === currentTrip ? 3 : 2,
                        zIndex: trip.index === currentTrip ? 2 : 1,
                    });

                    polyline.addListener('click', () => {
                        setCurrentTrip(index);
                    });

                    const stops = trip.stops.map((stop, index) => {

                        const path = index === trip.stops.length - 1 ? 'M -1,-1 1,-1 1,1 -1,1 z' : google.maps.SymbolPath.CIRCLE;
                        const fillColor = stop.id === currentStop?.id ?
                            '#ffffff' :
                            index === trip.stops.length - 1 || index === 0 ?
                                '#ffffff' :
                                trip.index === currentTrip ?
                                    currentRoute?.color :
                                    '#6d6d6d';
                        const strokeColor = stop.id === currentStop?.id ?
                            '#000000' :
                            trip.index === currentTrip ?
                                currentRoute?.color :
                                '#6d6d6d';
                        const strokeWeight = stop.id === currentStop?.id ? 3 :
                            index === trip.stops.length - 1 || index === 0 ? 2 : 1;


                        const marker: google.maps.Marker = new google.maps.Marker({
                            position: {
                                lat: stop.lat,
                                lng: stop.lng,
                            },
                            icon: {
                                path,
                                scale: stop.id === currentStop?.id ? 6 : 5,
                                fillColor,
                                fillOpacity: 1,
                                strokeColor,
                                strokeWeight,
                            },
                            zIndex: trip.index === currentTrip ? 2 : 1,
                            title: stop.stopName,
                        });

                        marker.addListener('click', () => {
                            history.push(`/stops/${stop.id}`);
                        });

                        return marker;
                    });

                    if (index === currentTrip) {
                        const bounds = new google.maps.LatLngBounds();
                        const points = polyline.getPath().getArray();
                        points.forEach((point) => bounds.extend(point));
                        map?.fitBounds(bounds);
                        if (currentStop) {
                            map?.panTo({lat: currentStop.lat, lng: currentStop.lng});
                        }
                    }

                    map?.drawTrip(polyline, trip.index === currentTrip ? stops : []);
                    if (currentRoute.slug === 'busamb') {
                        const tripHeadsign = currentRoute.tripsPaths[currentTrip]?.tripHeadsign;

                        const filteredRealTimes = Object.values(realTimes).flatMap((realtime) =>
                            realtime.realTimes[currentRoute.id]?.filter((rt) =>
                                rt.lineCode === currentRoute.shortName && rt.destination === tripHeadsign,
                            ) || [],
                        );
                        // map?.drawTripMarker(filteredRealTimes);
                        if (filteredRealTimes.length > 0) {
                            map?.drawTripMarker([filteredRealTimes[0]]);
                        }
                    }
                });
        }
    }, [map, currentStop, currentRoute, currentTrip, tripCoords]);

    useEffect(() => {

        if (map) {

            if (geolocationGranted) {
                setWarningLocationVisibility(MessageKeys.NONE);
                goToCurrentLocation();
            } else {
                setWarningLocationVisibility(MessageKeys.ENABLE);
            }
        }
    }, [map, geolocationGranted]);

    useEffect(() => {

        if (map && db) {
            map.bounds$
                .pipe(
                    debounceTime(300),
                    takeUntil(unsubscribe$),
                )
                .subscribe(boundsHandler);

            map.pointer$
                .pipe(
                    takeUntil(unsubscribe$),
                    skip(1),
                )
                .subscribe({
                    next: (pointer: google.maps.LatLngLiteral | null) => setPointer(pointer || map?.currentLocation || INIT_COORDS),
                });

            map.pointerCoords$
                .pipe(
                    takeUntil(unsubscribe$),
                )
                .subscribe(pointerHandler);
        }

        return () => {
            unsubscribe$.next(true);
            unsubscribe$.complete();
        };

    }, [map, db]);
    useEffect(() => {
        if (!map) return;
        if (db?.dataSources?.stops.slugFilter.BiciMap === true) {
            map.addTileLayer(
                'https://ide.amb.cat/geoserveis/rest/services/Mobilitat/xarxa_pedalable_3857/MapServer/tile/{z}/{y}/{x}?blankTile=false',
                {
                    alt: 'Test',
                    name: 'Test Layer',
                    maxZoom: 19,
                },
            );
        } else {
            map.removeTileLayer();
        }
    }, [db?.dataSources?.stops.slugFilter.BiciMap, map]);

    const [selectedStop, setSelectedStop] = useState<StopModel | null>(null);

    useEffect(() => {
        const pathIsStop = location.pathname.startsWith('/stops/');
        const stopId = pathIsStop ? location.pathname.split('/').pop() : null;

        if (!pathIsStop) {
            setSelectedStop(null);
        } else if (!selectedStop && stopId && db) {
            db.dataSources?.stops.findOneById(stopId).then((stop: any) => {
                if (stop) {
                    setSelectedStop(stop);
                }
            }).catch((error: any) => {
                console.error(`Error fetching stop with ID ${stopId}:`, error);
            });
        }

    }, [location]),

    useEffect(() => {
        let stops = view === MAP_VIEW.STOPS ? visibleStops : view === MAP_VIEW.CLOSE_STOPS ? closeStops : [];
        if (selectedStop && !stops.some((stop) => stop.stopId === selectedStop.stopId)) {
            stops = [...stops, selectedStop];
        }
        if (pinSet) {
            map?.drawMarkers(
                stops.map((stop) => {
                    const marker = new google.maps.Marker({
                        position: {
                            lat: stop.lat,
                            lng: stop.lng,
                        },
                        clickable: true,
                        icon: {
                            url: pinSet[stop.slug]?.pin,
                            scaledSize: new google.maps.Size(
                                stop.id === currentStop?.id ? MARKER_SELECTED_WIDTH : MARKER_WIDTH,
                                stop.id === currentStop?.id ? MARKER_SELECTED_HEIGHT : MARKER_HEIGHT,
                            ),
                        },
                        optimized: true,
                    });

                    marker.set('id', `${stop.id}${stop.id === currentStop?.id ? ':selected' : ''}`);

                    marker.addListener('click', () => {
                        setSelectedStop(stop);
                        history.push(`/stops/${stop.id}`);
                    });

                    return marker;
                })
                    .concat(currentStopEntries.map((entry) => {
                        const marker = new google.maps.Marker({
                            position: {
                                lat: entry.lat,
                                lng: entry.lng,
                            },
                            clickable: false,
                            icon: {
                                url: pinSet['entry'].pin,
                                scaledSize: new google.maps.Size(
                                    MARKER_WIDTH,
                                    MARKER_HEIGHT,
                                ),
                            },
                            optimized: true,
                        });

                        marker.set('id', entry.id);

                        return marker;
                    })),
            );
        }
    }, [pinSet, visibleStops, closeStops, currentStop, currentStopEntries, view, selectedStop]);


    useEffect(() => {
        if (currentStop) map?.zoomPosition({lat: currentStop.lat, lng: currentStop.lng});
    }, [currentStop]);

    useEffect(() => {

        drawRouteTrips();
    }, [tripCoords, currentRoute, currentTrip, currentStop]);

    useEffect(() => {
        db?.dataSources?.stops.setPointer(pointer);
    }, [pointer]);

    useEffect(() => {
        if (db && map && query.lat && query.lng) {
            const coords = {lat: Number(query.lat), lng: Number(query.lng)};
            map.setPointer(coords);
            db.dataSources?.stops.setPointer(coords);
        }
        // else {
        // goToCurrentLocation();
        // }
    }, [query, map, ready, db]);

    const markerTypes = {
        NO_STOP: 'no_stop',
        HOME: 'home',
        LOCATION: 'location',
        BUS: 'busamb',
        FGC: 'fgc',
    };


    function addClickMarker(
        newMarker: any,
        type: string,
        coord: any,
        infoWindowContent: any,
        markerInfo: any,
        redirect: any,
    ) {
        newMarker.addListener('click', () => {
            const pos = {
                lat: newMarker.getPosition()?.lat(),
                lng: newMarker.getPosition()?.lng(),
            };
            if (!!pos) {
                map?.setCenter(pos);
                map?.panBy(0, 250);
            }
            changeMarkerSize(newMarker, type, transportIcons);
            redirect({coord, type, infoWindowContent, markerInfo});
            hideOrShowOnMarkersOnViewport(markerList, map);
        });
    };


    // function for Create a marker into the map
    function createAccessMarker(coord: {}, type: string, infoWindowContent: any, redirect: any, markerInfo: any) {
        const newMarker = createAccesMaker(map, coord, type, markerInfo);
        // guardem la llista de marcadors
        accessList.push(newMarker);
        // addClickMarker(newMarker, type, coord, infoWindowContent, markerInfo, redirect);
        // If infoWindowContent exist, render infoWindowComponent whith the content
        addInfoWindowContentEvent(newMarker, map, infoWindowContent);
        setAcessList(accessList);
    }

    const getCurrentPositionCoords = async (): Promise<any> => {
        return new Promise((resolve, reject) => {
            if (navigator.geolocation) {
                navigator.geolocation.getCurrentPosition(
                    (position) => {
                        setWarningLocationVisibility(MessageKeys.NONE);
                        resolve({
                            lat: position.coords.latitude,
                            lng: position.coords.longitude,
                        });
                    },
                    () => {
                        setWarningLocationVisibility(MessageKeys.ENABLE);
                        if (!isMobile) {
                            alert(t('commons_dialog_enable_location_message'));
                        }
                    },
                );
            } else {
                // navegador no compatible
                setWarningLocationVisibility(MessageKeys.NO_SUPPORT);
                reject(new Error('Geolocation not supported by browser'));
            }
        });
    };


    const changeAccesColor = (markerID: any) => {
        changeMarkerAccesColor(markerID, accessList);
    };

    let lastInfoWindow: any;

    // Add Cick Event on marker, show inforWindow component when it's required
    function addInfoWindowContentEvent(marker: any, map: any, infoWindowContent: any) {
        const infoWindow = ReactDOMServer.renderToString(<InfoWindow value={infoWindowContent}/>);

        const infowindow = new google.maps.InfoWindow({
            content: infoWindow,
        });

        const clickInfoWindow = () => {
            const markerId = marker.stop_data.id;
            if (!!lastInfoWindow) {
                lastInfoWindow.close();
            }
            changeAccesColor(markerId);
            infowindow.open(map, marker);
            lastInfoWindow = infowindow;
        };
        marker.addListener('click', clickInfoWindow);
    };

    const transportIcons: any = {};

    function createMarker(coord: {}, infoWindowContent: any, redirect: any, markerInfo: any, hideMarker: boolean = false, type: string = 'location') {
        const newMarker = createMaker(map, coord, type, markerInfo, transportIcons);
        // guardem la llista de marcadors
        (hideMarker) ? newMarker.setMap(null) : null;
        (hideMarker) ? newMarker.set('layerActive', false) : null;
        (!markerList[type]) ? markerList[type] = [] : markerList[type];
        markerList[type].push(newMarker);

        addClickMarker(newMarker, type, coord, infoWindowContent, markerInfo, redirect);
        // If infoWindowContent exist, render infoWindowComponent whith the content
        (!!infoWindowContent) ? addInfoWindowContentEvent(newMarker, map, infoWindowContent) : null;

        setMarkerList(markerList);
        const tmpmarkerType: any = markerType;
        tmpmarkerType[type] = markerList;
        setMarkerType(markerType);
    }

    const getTopNearMarkersByType = (markerlist: {
        [index: string]: any
    }, latLng: google.maps.LatLng, type: string) => {
        const distancePerMarker = markerlist[type]?.map((marker: google.maps.Marker) => {
            const distance = google.maps.geometry.spherical.computeDistanceBetween(
                marker.getPosition() as google.maps.LatLng,
                latLng,
            );
            return [distance, marker];
        });
        distancePerMarker?.sort((a: any, b: any) => a[0] - b[0]);
        return distancePerMarker?.slice(0, 5).map((item: any) => item[1].get('stop_data'));
    };


    const getNearMarkersData = (latitude: number, longitude: number, stopType: string = markerTypes.BUS) => {
        const latLng = new google.maps.LatLng(latitude, longitude);
        const nearItems = getTopNearMarkersByType(markerList, latLng, stopType);
        return nearItems;
    };

    const deleteMarkerLocation = (markerList: any) => {
        if (markerList['location']?.length) {
            markerList['location'][0].setMap(null);
        }
    };

    function deleteLocationMarker() {
        deleteMarkerLocation(markerList);
    }

    const centerMapToCoordenates = (latitude: number, longitude: number) => {
        const googleCoords = new google.maps.LatLng(latitude, longitude);
        map.setCenter(googleCoords);
        map.panTo(googleCoords);
    };

    const drawZbe = () => {
        map?.drawZbeInMap();
    };

    const removeZbe = () => {
        map?.removeZbeInMap();
    };

    const activateNoParkingLayer = () => {
        if (map && !isNoParkingActive) {
            map.loadKmlLayer('https://opendata.ambmobilitat.cat/KML/noparking_moto_sh.kml');
            setIsNoParkingActive(true);
        }
    };

    const deactivateNoParkingLayer = () => {
        if (map && isNoParkingActive) {
            map.removeKmlLayer();
            setIsNoParkingActive(false);
        }
    };

    return <MapProvider value={{
        map,
        ready,
        init,
        geolocationGranted,
        warningLocationVisibility,
        goToCurrentLocation,
        currentLocation,
        setView,
        pointer,
        getNearMarkersData,
        createMarker,
        getCurrentPositionCoords,
        deleteLocationMarker,
        centerMapToCoordenates,
        drawZbe,
        removeZbe,
        isNoParkingActive,
        activateNoParkingLayer,
        deactivateNoParkingLayer,
        setIsNoParkingActive,
        setNoParkingActive,
        noParkingActive,
        setTransportListActivatedOnce,
        transportListActivatedOnce,
    }}>
        {props.children}
    </MapProvider>;
};
